import { ImageDisplay, ImageWithType, IntegrationException } from '../../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';

import { detectMobileViewport } from '../../helpers/device';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgZoom from 'lightgallery/plugins/zoom';
import lightGallery from 'lightgallery';
import topicData from '../../helpers/state/topic-data';

@customElement('image-carousel')
export class ImageCarousel extends LitElement {
    @query('#carouselImgContainer')
    slidesContainer?: HTMLElement;

    @query('#carouselLeftArrow')
    carouselLeftArrow!: HTMLElement;

    @query('#carouselRightArrow')
    carouselRightArrow!: HTMLElement;

    @property({ type: Array })
    imageUrls: ImageWithType[] | string[] = [];

    @property({ type: Number })
    borderRadius: Number = 0;

    @property({ type: Boolean })
    slideshow: boolean = false;

    @property({ type: String })
    height: string = '300px';

    @state()
    page: number = 0;

    @state()
    mobilePreview: boolean = false;

    private lightGalleryInstance?: ReturnType<typeof lightGallery>;
    private lightGalleryListenersBound = false;
    private lightGalleryImageKey?: string;
    private slideshowTimer?: number;

    private computeImageKey(): string {
        return (this.imageUrls as Array<ImageWithType | string>)
            .map(i => (typeof i === 'string' ? i : i.image))
            .join('|');
    }

    get isMobile() {
        return detectMobileViewport(this.mobilePreview);
    }

    handleImageError(e: Event) {
        // send event to parent component to handle
        const event = new CustomEvent('image-error', {
            detail: e,
            bubbles: true,
            composed: true
        });
        this.dispatchEvent(event);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.lightGalleryInstance?.destroy();
        this.lightGalleryInstance = undefined;
        if (this.slideshowTimer !== undefined) {
            clearInterval(this.slideshowTimer);
            this.slideshowTimer = undefined;
        }
    }

    async updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('imageUrls')) {

            // The card hands us a fresh `displayableImageUrls` array on every
            // re-render, so Lit re-runs this even when the photos are identical.
            // Skip the rebuild when the image set is unchanged — destroy() would
            // otherwise close a lightbox the user has open.
            const imageKey = this.computeImageKey();
            if (this.lightGalleryInstance && imageKey === this.lightGalleryImageKey) {
                return;
            }
            this.lightGalleryImageKey = imageKey;

            // this lightgallery opens when an image from the carousel is clicked - this creates a full screen takeover
            const lgc = document.querySelector('#lgc') as HTMLElement;

            if (this.slidesContainer) {
                // Tear down the previous gallery first; re-initialising without this
                // stacks a new instance (and a new .lg-container) on every imageUrls
                // change, so one click opened several lightboxes at once.
                this.lightGalleryInstance?.destroy();

                if (!this.lightGalleryListenersBound) {
                    this.slidesContainer.addEventListener('lgAfterOpen', () => {
                        lgc.style.pointerEvents = 'auto';
                    });

                    this.slidesContainer.addEventListener('lgAfterClose', () => {
                        lgc.style.pointerEvents = 'none';
                    });

                    this.lightGalleryListenersBound = true;
                }

                this.lightGalleryInstance = lightGallery(this.slidesContainer, {
                    plugins: [lgZoom, lgThumbnail],
                    speed: 500,
                    container: lgc,
                    thumbnail: true,
                    showCloseIcon: true,
                    swipeToClose: true,
                    mobileSettings: {
                        controls: true,
                        showCloseIcon: true,
                        download: false,
                    },
                    isMobile: () => this.isMobile,
                    licenseKey: 'C1972A6F-557E4D6B-BC47AA64-F56DC8F4',
                });

                lgc.style.position = 'absolute';
            }

            if (this.slideshow) {
                if (this.slideshowTimer !== undefined) {
                    clearInterval(this.slideshowTimer);
                }
                this.slideshowTimer = window.setInterval(() => {
                    this.handleNextArrow();
                }, 5000);
            }
        }
    }

    private handlePrevArrow() {
        this.page--;

        if (this.slidesContainer) {
            const slideWidth = this.slidesContainer?.clientWidth ?? 0;
            this.slidesContainer.scroll({left: slideWidth * this.page, behavior:'smooth'});
        }

    }

    private handleNextArrow() {
        this.page++;
        if (this.slidesContainer) {
            const slideWidth = this.slidesContainer?.clientWidth ?? 0;
            this.slidesContainer.scroll({left: (slideWidth * this.page), behavior:'smooth'});
        }
    }

    isImageWithType(image: any): image is ImageWithType {
        return (image as ImageWithType).type !== undefined && (image as ImageWithType).image !== undefined;
    }

    protected render() {
        const showLeftArrow = this.page > 0;
        const showRightArrow = this.page < this.imageUrls.length - 1;

        return html`
            <div class="${detectMobileViewport(this.mobilePreview) ? 'carousel__wrapper__mobile' : 'carousel__wrapper'}" style="max-height: min(33vh, ${this.height});">
                ${this.slideshow ? '' : html`
                    <button id="carouselLeftArrow" class="carousel__arrow" @click="${this.handlePrevArrow}" ?disabled=${this.page === 0}>
                        ${showLeftArrow ? html`&#8249; ` : ''}
                    </button>

                    <button id="carouselRightArrow" class="carousel__arrow carousel__arrow--right" @click="${this.handleNextArrow}" ?disabled=${this.page === this.imageUrls.length - 1 || topicData.hasExceptions === IntegrationException.AustraliaMeatAndLivestock}>
                        ${showRightArrow ? html`&#8250;` : ''}
                    </button>
                `}
                <div class="carousel__image-container" id="carouselImgContainer" style="border-top-left-radius: ${this.borderRadius}px; border-top-right-radius: ${this.borderRadius}px;">
                    ${this.imageUrls.length > 0 && this.isImageWithType(this.imageUrls[0]) ? this.renderImageWithTypes(this.imageUrls as ImageWithType[]) : this.renderImages(this.imageUrls as string[])}

                </div>
            </div>`;
    }

    renderImageWithTypes(images: ImageWithType[]) {
        return html`
            ${images.map(image =>
                    image.type === 'EMBED' ?
                    html`
                        <div class="carousel__image-wrapper">
                            <iframe width="100%" height="100%" src="${image.image}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
                        </div>
                    `:
                    html`<a href=${image.image} class="carousel__image-wrapper">
                        ${topicData.topic?.image_display === ImageDisplay.ContainBlur ? html`
                            <div class="carousel__image-background" style="background-image: url(${image.image});"></div>
                        ` : ''}
                        <img @error="${(e: Event) => this.handleImageError(e)}" class="carousel__image ${image.type ?? 'USER'}" src=${image.image} style="object-fit: ${topicData.topic?.image_display=== ImageDisplay.Cover ? 'cover' : 'contain'}; background-color: ${image.type === 'GOOGLE' ? 'black' : 'white'}"/>
                        <mwc-icon id="fullScreen" class="carousel__arrow carousel__arrow--right material-symbols-outlined">
                            open_in_full
                        </mwc-icon>
                    </a>`
                )}
        `;
    }

    renderImages(images: string[]) {
        return html`
            ${images.map(image => html`
                <a href=${image} class="carousel__image-wrapper">
                    ${topicData.topic?.image_display === ImageDisplay.ContainBlur ? html`
                        <div class="carousel__image-background" style="background-image: url(${image});"></div>
                    ` : ''}
                    <img class="carousel__image USER" src=${image} style="object-fit: ${topicData.topic?.image_display=== ImageDisplay.Cover ? 'cover' : 'contain'}; background-color: white"/>
                    <mwc-icon id="fullScreen" class="carousel__arrow carousel__arrow--right material-symbols-outlined">
                        open_in_full
                    </mwc-icon>
                </a>
            `)}
        `;
    }


    static get styles() {
        return css`
            .carousel__wrapper {
                height: var(--carousel-height, 300px);
                max-height: min(33vh, 300px);
            }

            .carousel__image-container {
                height: var(--carousel-height, 100%);
                width: 100%;
                display: flex;
                scroll-behavior: smooth;
                /* Native horizontal swipe on touch devices. The arrow
                   buttons still drive it via .scroll({left}); scroll-snap
                   keeps both paths landing cleanly on a slide. */
                overflow-x: auto;
                overflow-y: hidden;
                scroll-snap-type: x mandatory;
                -webkit-overflow-scrolling: touch;
                scrollbar-width: none;
            }

            .carousel__image-container::-webkit-scrollbar {
                display: none;
            }

            .carousel__image {
                width: 100%;
                height: 100%;
                position: relative;
            }

            .carousel__image.GOOGLE {
                object-fit: cover;
            }

            .carousel__image.USER {
                object-fit: contain;
            }

            .carousel__image-wrapper {
                height: 100%;
                width: 100%;
                flex: 1 0 100%;
                overflow: hidden;
                position: relative;
                scroll-snap-align: start;
            }

            .carousel__arrow {
                position: absolute;
                top: 0px;
                bottom: 0px;
                margin: auto;
                background-color: transparent;
                border: none;
                cursor: pointer;
                font-size: 50px;
                z-index: 1;
            }

            .carousel__image-background {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background-size: cover;
                background-position: center;
                filter: blur(2px);
            }

            .carousel__arrow--right {
                right: 0;
            }

            #fullScreen {
                bottom: 0;
                top: auto;
                -webkit-text-stroke: 0.5px white;
                font-size: 24px;
                --mdc-icon-size: 24px;
                padding: 5px;
                color: black;
            }

            .carousel__arrow:hover {
                color: black;
                opacity: .5;
            }

            /* Nav buttons only (not #fullScreen, which is an mwc-icon).
               A translucent disc keeps the chevron readable over any
               photo. Hidden entirely at the ends so an empty disc never
               shows. */
            button.carousel__arrow {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 36px;
                height: 36px;
                top: 50%;
                bottom: auto;
                transform: translateY(-50%);
                margin: 0 8px;
                border-radius: 50%;
                background-color: rgba(0, 0, 0, 0.45);
                color: #fff;
                font-size: 28px;
                line-height: 1;
                padding: 0 0 4px;
            }

            button.carousel__arrow:hover {
                color: #fff;
                opacity: 1;
                background-color: rgba(0, 0, 0, 0.6);
            }

            button.carousel__arrow:disabled {
                display: none;
            }

            .carousel__wrapper__mobile {
                height: 225px;
            }
        `;
    }
}
